<template>
  <NavBar />
  <v-container fluid style="margin-top: 185px;">
    <v-row>
      <v-col cols="12">
        <div class="d-flex justify-space-between align-center mb-4">
          <h1 class="text-h4">Linkuri Referral</h1>
          <v-btn color="primary" @click="openCreateDialog">
            <v-icon start>mdi-plus</v-icon>
            Creeaza Link Nou
          </v-btn>
        </div>
      </v-col>
    </v-row>

    <!-- Client Selector (admin/root only) -->
    <v-row v-if="isAdmin" class="mb-2" align="center">
      <v-col cols="12" md="4">
        <v-autocomplete
          v-model="selectedClientId"
          label="Selecteaza Client"
          :items="parentClientsForSelect"
          :loading="loadingParentClients"
          placeholder="Clientul meu"
          clearable
          dense
          outlined
          hide-details
          auto-select-first
          @update:model-value="onClientChanged"
        ></v-autocomplete>
      </v-col>
      <v-col cols="12" md="3">
        <v-switch
          v-model="includeSubclients"
          label="Include subclienti"
          color="primary"
          hide-details
          density="compact"
          @update:model-value="onClientChanged"
        />
      </v-col>
    </v-row>

    <!-- Stats Cards -->
    <v-row>
      <v-col cols="12" md="3">
        <v-card>
          <v-card-text class="text-center">
            <div class="text-h4 font-weight-bold text-primary">
              {{ stats?.total_referral_links || 0 }}
            </div>
            <div class="text-caption">Total Linkuri</div>
          </v-card-text>
        </v-card>
      </v-col>
      <v-col cols="12" md="3">
        <v-card>
          <v-card-text class="text-center">
            <div class="text-h4 font-weight-bold text-success">
              {{ stats?.total_referrals || 0 }}
            </div>
            <div class="text-caption">Total Referrals</div>
          </v-card-text>
        </v-card>
      </v-col>
      <v-col cols="12" md="3">
        <v-card>
          <v-card-text class="text-center">
            <div class="text-h4 font-weight-bold text-info">
              {{ stats?.rewarded_conversions || 0 }}
            </div>
            <div class="text-caption">Conversii Recompensate</div>
          </v-card-text>
        </v-card>
      </v-col>
      <v-col cols="12" md="3">
        <v-card>
          <v-card-text class="text-center">
            <div class="text-h4 font-weight-bold text-warning">
              {{ stats?.pending_conversions || 0 }}
            </div>
            <div class="text-caption">In Asteptare</div>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <!-- Links Table -->
    <v-row class="mt-4">
      <v-col cols="12">
        <v-card>
          <v-card-title>
            Linkurile Mele
            <v-spacer />
            <v-btn icon="mdi-refresh" variant="text" @click="loadLinks" :loading="loading" />
          </v-card-title>
          <v-card-text>
            <v-data-table
              :headers="linkHeaders"
              :items="referralLinks"
              :loading="loading"
              class="elevation-1"
            >
              <template #[`item.effective_code`]="{ item }">
                <v-chip color="primary" variant="outlined" @click="copyCode(item.effective_code)">
                  {{ item.effective_code }}
                  <v-icon end size="small">mdi-content-copy</v-icon>
                </v-chip>
              </template>
              <template #[`item.full_url`]="{ item }">
                <v-btn
                  variant="text"
                  size="small"
                  color="primary"
                  @click="copyUrl(item.full_url)"
                >
                  Copiaza URL
                  <v-icon end size="small">mdi-link</v-icon>
                </v-btn>
              </template>
              <template #[`item.usage`]="{ item }">
                {{ item.current_uses }} / {{ item.max_uses || 'Nelimitat' }}
              </template>
              <template #[`item.active`]="{ item }">
                <v-chip :color="item.active ? 'success' : 'error'" size="small">
                  {{ item.active ? 'Activ' : 'Inactiv' }}
                </v-chip>
              </template>
              <template #[`item.signup_bonus_amount`]="{ item }">
                {{ item.signup_bonus_amount ? formatCurrency(item.signup_bonus_amount) : '-' }}
              </template>
              <template #[`item.commission_bonus_percentage`]="{ item }">
                {{ item.commission_bonus_percentage ? `+${item.commission_bonus_percentage}%` : '-' }}
              </template>
              <template #[`item.commission_bonus_duration_days`]="{ item }">
                {{ item.commission_bonus_duration_days || 30 }}
              </template>
              <template #[`item.expires_at`]="{ item }">
                {{ item.expires_at ? formatDate(item.expires_at) : 'Permanent' }}
              </template>
              <template #[`item.actions`]="{ item }">
                <v-btn icon="mdi-pencil" size="small" variant="text" @click="openEditDialog(item)" />
              </template>
            </v-data-table>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <!-- Referrals List -->
    <v-row class="mt-4">
      <v-col cols="12">
        <v-card>
          <v-card-title>Clienti Referiti</v-card-title>
          <v-card-text>
            <v-data-table
              :headers="referralHeaders"
              :items="referrals"
              :loading="loadingReferrals"
              class="elevation-1"
            >
              <template #[`item.status`]="{ item }">
                <v-chip :color="getStatusColor(item.status)" size="small">
                  {{ item.status }}
                </v-chip>
              </template>
              <template #[`item.signup_date`]="{ item }">
                {{ formatDate(item.signup_date) }}
              </template>
              <template #[`item.first_purchase_amount`]="{ item }">
                {{ item.first_purchase_amount ? formatCurrency(item.first_purchase_amount) : '-' }}
              </template>
              <template #[`item.signup_bonus_paid`]="{ item }">
                <v-icon
                  :color="item.signup_bonus_paid ? 'success' : 'grey'"
                  :icon="item.signup_bonus_paid ? 'mdi-check-circle' : 'mdi-clock-outline'"
                />
              </template>
            </v-data-table>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <!-- Create Link Dialog -->
    <v-dialog v-model="createDialog" max-width="600">
      <v-card>
        <v-card-title>Creeaza Link Referral Nou</v-card-title>
        <v-card-text>
          <v-form ref="createForm">
            <v-autocomplete
              v-if="isAdmin"
              v-model="newLink.client_id"
              label="Client (proprietar campanie)"
              :items="parentClientsForSelect"
              :loading="loadingParentClients"
              placeholder="Selecteaza clientul"
              clearable
              hide-details
              class="mb-4"
              :rules="[v => !!v || 'Selecteaza un client']"
            />
            <v-text-field
              v-model="newLink.custom_slug"
              label="Cod personalizat (optional)"
              hint="Ex: JOHN2024 - va crea link-ul cu acest cod"
              persistent-hint
              class="mb-4"
            />
            <v-text-field
              v-model.number="newLink.signup_bonus_amount"
              label="Bonus la inregistrare (EUR)"
              type="number"
              hint="Suma pe care o primesti cand cineva se inscrie folosind link-ul tau"
              persistent-hint
              class="mb-4"
            />
            <v-text-field
              v-model.number="newLink.commission_bonus_percentage"
              label="Bonus comision extra (%)"
              type="number"
              hint="Procent extra de comision pe prima achizitie a clientului referit"
              persistent-hint
              class="mb-4"
            />
            <v-text-field
              v-model.number="newLink.commission_bonus_duration_days"
              label="Durata bonus comision (zile)"
              type="number"
              hint="Nr. de zile in care bonusul de comision este activ dupa inregistrare (default: 30)"
              persistent-hint
              class="mb-4"
            />
            <v-text-field
              v-model.number="newLink.max_uses"
              label="Numar maxim de utilizari (optional)"
              type="number"
              hint="Lasa gol pentru nelimitat"
              persistent-hint
              class="mb-4"
            />
            <v-text-field
              v-model="newLink.campaign_name"
              label="Nume campanie (optional)"
              hint="Pentru a identifica usor link-ul"
              persistent-hint
              class="mb-4"
            />
            <v-text-field
              v-model="newLink.expires_at"
              label="Data expirare (optional)"
              type="date"
              class="mb-4"
            />
          </v-form>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn variant="text" @click="createDialog = false">Anuleaza</v-btn>
          <v-btn color="primary" @click="submitCreateLink" :loading="creatingLink">
            Creeaza Link
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Edit Link Dialog -->
    <v-dialog v-model="editDialog" max-width="600">
      <v-card>
        <v-card-title>Editeaza Link Referral</v-card-title>
        <v-card-text v-if="editingLink">
          <v-alert type="info" variant="tonal" class="mb-4">
            Cod: <strong>{{ editingLink.effective_code }}</strong>
          </v-alert>
          <v-form ref="editForm">
            <v-text-field
              v-model.number="editForm.signup_bonus_amount"
              label="Bonus la inregistrare (EUR)"
              type="number"
              hint="Suma pe care o primesti cand cineva se inscrie folosind link-ul tau"
              persistent-hint
              class="mb-4"
            />
            <v-text-field
              v-model.number="editForm.commission_bonus_percentage"
              label="Bonus comision extra (%)"
              type="number"
              hint="Procent extra de comision pe prima achizitie a clientului referit"
              persistent-hint
              class="mb-4"
            />
            <v-text-field
              v-model.number="editForm.commission_bonus_duration_days"
              label="Durata bonus comision (zile)"
              type="number"
              hint="Nr. de zile in care bonusul de comision este activ dupa inregistrare"
              persistent-hint
              class="mb-4"
            />
            <v-text-field
              v-model.number="editForm.max_uses"
              label="Numar maxim de utilizari (optional)"
              type="number"
              hint="Lasa gol pentru nelimitat"
              persistent-hint
              class="mb-4"
            />
            <v-text-field
              v-model="editForm.campaign_name"
              label="Nume campanie (optional)"
              hint="Pentru a identifica usor link-ul"
              persistent-hint
              class="mb-4"
            />
            <v-text-field
              v-model="editForm.expires_at"
              label="Data expirare (optional)"
              type="date"
              class="mb-4"
            />
            <v-switch
              v-model="editForm.active"
              label="Link activ"
              color="success"
              class="mb-4"
            />
          </v-form>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn variant="text" @click="editDialog = false">Anuleaza</v-btn>
          <v-btn color="primary" @click="submitEditLink" :loading="updatingLink">
            Salveaza
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-snackbar v-model="snackbar" :color="snackbarColor" timeout="3000">
      {{ snackbarMessage }}
    </v-snackbar>
  </v-container>
</template>

<script>
import { getMyReferralLinks, createReferralLink, updateReferralLink, getMyReferrals } from '@/composables/useMlmAPI'
import { getClientTree } from '@/composables/useGetClientsAPI'
import NavBar from '@/components/NavBar.vue'
import store from '@/store'

export default {
  name: 'ReferralLinksView',
  components: { NavBar },
  data() {
    return {
      // Data
      referralLinks: [],
      referrals: [],
      stats: null,
      user: null,

      // Client selector (for admin/root)
      selectedClientId: null,
      includeSubclients: true,
      parentClientsList: [],
      loadingParentClients: false,

      // Loading
      loading: false,
      loadingReferrals: false,
      creatingLink: false,

      // Dialogs
      createDialog: false,
      editDialog: false,
      newLink: {
        client_id: null,
        custom_slug: '',
        signup_bonus_amount: 0,
        commission_bonus_percentage: 0,
        commission_bonus_duration_days: 30,
        max_uses: null,
        campaign_name: '',
        expires_at: null
      },
      editingLink: null,
      editForm: {
        signup_bonus_amount: 0,
        commission_bonus_percentage: 0,
        commission_bonus_duration_days: 30,
        max_uses: null,
        campaign_name: '',
        expires_at: null,
        active: true
      },
      updatingLink: false,

      // Snackbar
      snackbar: false,
      snackbarMessage: '',
      snackbarColor: 'success',

      linkHeaders: [
        { title: 'Client', key: 'client_name' },
        { title: 'Cod', key: 'effective_code' },
        { title: 'URL', key: 'full_url' },
        { title: 'Campanie', key: 'campaign_name' },
        { title: 'Utilizari', key: 'usage' },
        { title: 'Bonus Signup', key: 'signup_bonus_amount' },
        { title: 'Bonus Comision', key: 'commission_bonus_percentage' },
        { title: 'Durata Bonus (zile)', key: 'commission_bonus_duration_days' },
        { title: 'Expira', key: 'expires_at' },
        { title: 'Status', key: 'active' },
        { title: 'Actiuni', key: 'actions', sortable: false }
      ],

      referralHeaders: [
        { title: 'Client', key: 'referred_client_name' },
        { title: 'Cod Referral', key: 'referral_code' },
        { title: 'Data Inscriere', key: 'signup_date' },
        { title: 'Prima Achizitie', key: 'first_purchase_amount' },
        { title: 'Bonus Platit', key: 'signup_bonus_paid' },
        { title: 'Status', key: 'status' }
      ]
    }
  },
  computed: {
    token() {
      return this.$store.getters.getToken
    },
    isAdmin() {
      return this.user?.is_admin || this.user?.admin || false
    },
    parentClientsForSelect() {
      return this.parentClientsList.map(item => ({
        value: item.id,
        title: item.displayName
      }))
    },
    effectiveClientId() {
      return this.selectedClientId || this.user?.client_id
    }
  },
  mounted() {
    this.user = store.getters.getUser
    this.loadLinks()
    this.loadReferrals()
    if (this.isAdmin) {
      this.fetchParentClients()
    }
  },
  methods: {
    async loadLinks() {
      this.loading = true
      const result = await getMyReferralLinks(this.token, this.effectiveClientId, this.includeSubclients)
      if (!result.error) {
        this.referralLinks = result.referral_links
      }
      this.loading = false
    },

    async loadReferrals() {
      this.loadingReferrals = true
      const result = await getMyReferrals(this.token, this.effectiveClientId, this.includeSubclients)
      if (!result.error) {
        this.referrals = result.referrals
        this.stats = result.stats
      }
      this.loadingReferrals = false
    },

    async fetchParentClients() {
      try {
        this.loadingParentClients = true
        const response = await getClientTree(this.token, this.user?.client_id)
        if (!response.error && response.clients_list) {
          this.parentClientsList = this.buildParentClientsList(response.clients_list)
        }
      } catch (error) {
        console.error("Error loading parent clients:", error)
      } finally {
        this.loadingParentClients = false
      }
    },

    buildParentClientsList(clientsTree) {
      const list = []
      const addClientsRecursively = (clients, level = 0) => {
        if (!clients || clients.length === 0) return
        clients.forEach(client => {
          const indent = level > 0 ? '\u2014'.repeat(level) + ' ' : ''
          list.push({
            id: client.client_id || client.id,
            name: client.name,
            displayName: indent + client.name,
            level: level
          })
          if (client.children && client.children.length > 0) {
            addClientsRecursively(client.children, level + 1)
          }
        })
      }
      addClientsRecursively(clientsTree)
      return list
    },

    onClientChanged() {
      this.loadLinks()
      this.loadReferrals()
    },

    openCreateDialog() {
      this.newLink = {
        client_id: this.selectedClientId || null,
        custom_slug: '',
        signup_bonus_amount: 0,
        commission_bonus_percentage: 0,
        commission_bonus_duration_days: 30,
        max_uses: null,
        campaign_name: '',
        expires_at: null
      }
      this.createDialog = true
    },

    openEditDialog(link) {
      this.editingLink = link
      this.editForm = {
        signup_bonus_amount: link.signup_bonus_amount || 0,
        commission_bonus_percentage: link.commission_bonus_percentage || 0,
        commission_bonus_duration_days: link.commission_bonus_duration_days || 30,
        max_uses: link.max_uses,
        campaign_name: link.campaign_name || '',
        expires_at: link.expires_at ? link.expires_at.split('T')[0] : null,
        active: link.active
      }
      this.editDialog = true
    },

    async submitEditLink() {
      this.updatingLink = true
      const result = await updateReferralLink(this.token, this.editingLink.id, this.editForm)
      this.updatingLink = false

      if (result.error) {
        this.snackbarMessage = result.message
        this.snackbarColor = 'error'
      } else {
        this.snackbarMessage = 'Link actualizat cu succes!'
        this.snackbarColor = 'success'
        this.editDialog = false
        this.loadLinks()
      }
      this.snackbar = true
    },

    async submitCreateLink() {
      this.creatingLink = true
      const linkData = { ...this.newLink }
      // Use client_id from the dialog selector, fallback to page-level selector, then user's own client
      linkData.client_id = this.newLink.client_id || this.selectedClientId || this.user?.client_id
      const result = await createReferralLink(this.token, linkData)
      this.creatingLink = false

      if (result.error) {
        this.snackbarMessage = result.message
        this.snackbarColor = 'error'
      } else {
        this.snackbarMessage = `Link creat cu succes! Cod: ${result.referral_code}`
        this.snackbarColor = 'success'
        // Update page-level selector to show the client we just created for
        if (this.newLink.client_id && this.newLink.client_id !== this.selectedClientId) {
          this.selectedClientId = this.newLink.client_id
        }
        this.createDialog = false
        this.loadLinks()
        this.loadReferrals()
      }
      this.snackbar = true
    },

    copyCode(code) {
      navigator.clipboard.writeText(code)
      this.snackbarMessage = 'Cod copiat!'
      this.snackbarColor = 'info'
      this.snackbar = true
    },

    copyUrl(url) {
      navigator.clipboard.writeText(url)
      this.snackbarMessage = 'URL copiat!'
      this.snackbarColor = 'info'
      this.snackbar = true
    },

    formatCurrency(value) {
      return new Intl.NumberFormat('ro-RO', { style: 'currency', currency: 'EUR' }).format(value || 0)
    },

    formatDate(dateStr) {
      if (!dateStr) return '-'
      return new Date(dateStr).toLocaleDateString('ro-RO', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      })
    },

    getStatusColor(status) {
      const colors = {
        pending: 'warning',
        qualified: 'info',
        rewarded: 'success',
        expired: 'error'
      }
      return colors[status] || 'grey'
    }
  }
}
</script>
